import type { DecisionSection } from '@zerohuman/ui'
import type { GateRow } from '@/api'

/**
 * A gate's summary as its decision card lays it out. `os.set_gate_summary` asks for markdown under headings
 * (`## What changed`, `## Watch out for`), so each heading becomes a section and its bullets its lines. Text
 * before the first heading, or a summary with none (a browser gate's one line), is a section with no
 * heading. A heading nothing follows is dropped: there is nothing under it to read.
 */
export function gateSummarySections(summary: string | null | undefined): DecisionSection[] {
    const sections: { heading?: string; lines: string[] }[] = []
    let current: { heading?: string; lines: string[] } | null = null
    for (const raw of (summary ?? '').split('\n')) {
        const line = raw.trim()
        if (!line) {
            continue
        }
        const heading = /^#{1,6}\s+(.+)$/.exec(line)?.[1]
        if (heading) {
            current = { heading: heading.trim(), lines: [] }
            sections.push(current)
            continue
        }
        if (!current) {
            current = { lines: [] }
            sections.push(current)
        }
        current.lines.push(line.replace(/^[-*+]\s+/, ''))
    }
    return sections.filter(section => section.lines.length > 0)
}

/**
 * What a Gates card needs to decide an escalated email without opening its run. The escalation payload already
 * carries the sender, subject, summary and suggested action; use those as structured sections even when the run
 * skipped its separate `os.set_gate_summary` call. Every other gate keeps using its stored summary unchanged.
 */
export function gateDecisionSections(gate: Pick<GateRow, 'toolName' | 'payload' | 'summary'>): DecisionSection[] {
    if (gate.toolName !== 'os.escalate_mail' || !isRecord(gate.payload)) {
        return gateSummarySections(gate.summary)
    }

    const from = textField(gate.payload, 'fromAddress')
    const subject = textField(gate.payload, 'subject')
    const summary = textField(gate.payload, 'summary')
    const suggestion = textField(gate.payload, 'suggestion')
    const sections: DecisionSection[] = []

    const email = [from ? `From: ${from}` : '', subject ? `Subject: ${subject}` : ''].filter(Boolean)
    if (email.length > 0) sections.push({ heading: 'Email', lines: email })
    if (summary) sections.push({ heading: 'Summary', lines: [summary] })
    if (suggestion) sections.push({ heading: 'Suggested action', lines: [suggestion] })

    return sections.length > 0 ? sections : gateSummarySections(gate.summary)
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function textField(row: Record<string, unknown>, key: string): string {
    const value = row[key]
    return typeof value === 'string' ? value.trim() : ''
}
